import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, useNavigate, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import Header from './components/Header';
import TabBar from './components/TabBar';
import Login from './components/Login';
import Dashboard from './modules/Dashboard';
import OrdersList from './modules/OrdersList';
import OrderDetail from './modules/OrderDetail';
import UserManagement from './modules/UserManagement';
import UserDetail from './modules/UserDetail';
import UserForm from './modules/UserForm';
import UserProfile from './modules/UserProfile';
import CustomerManagement from './modules/CustomerManagement';
import CustomerForm from './modules/CustomerForm';
import CustomerDetail from './modules/CustomerDetail';
import MediaPricelist from './modules/MediaPricelist';
import MediaForm from './modules/MediaForm';
import ProjectForm from './modules/ProjectForm';
import MediaPricelistDetail from './modules/MediaPricelistDetail';
import PricelistItems from './modules/PricelistItems';
import RatecardForm from './modules/RatecardForm';
import OrderForm from './modules/OrderForm';
import InvoiceList from './modules/InvoiceList';
import InvoiceDetail from './modules/InvoiceDetail';
import PaymentMatching from './modules/PaymentMatching';
import Settings from './modules/Settings';
import Spiglovani from './modules/Spiglovani';
import Statistics from './modules/Statistics';
import StatisticsSettings from './modules/StatisticsSettings';
import ToastNotification from './components/ToastNotification';
import ConfirmDialog from './components/ConfirmDialog';
import api from './services/api';

const TABS_STORAGE_KEY = 'regvyd_tabs';
const ACTIVE_TAB_STORAGE_KEY = 'regvyd_active_tab';
const TAB_SAVE_DEBOUNCE_MS = 2000; // Debounce API saves by 2 seconds
const DEFAULT_TABS = [
  { id: 'dashboard', title: 'Dashboard', path: '/dashboard', component: 'Dashboard' }
];
/** Kolik záložek se drží; přes limit se zahazují ty nejstarší (vpravo). */
const MAX_TABU = 50;

/**
 * Nová záložka se vkládá hned za Dashboard — nejnovější je tak vlevo a člověk
 * ji má po ruce, místo aby ji hledal na konci řady.
 */
const vlozZaDashboard = (tabs, novy) => {
  const i = tabs.findIndex(tab => tab.id === 'dashboard');
  const kam = i >= 0 ? i + 1 : 0;

  return [...tabs.slice(0, kam), novy, ...tabs.slice(kam)];
};

/**
 * Ořez na `MAX_TABU`. Zahazují se nejstarší záložky, tedy ty na konci řady;
 * `chraneneId` (typicky právě otevřená) se ale zahodit nesmí.
 */
const orezNaLimit = (tabs, chraneneId = null) => {
  if (tabs.length <= MAX_TABU) return tabs;

  const orezane = tabs.slice(0, MAX_TABU);
  if (chraneneId && !orezane.some(tab => tab.id === chraneneId)) {
    const chraneny = tabs.find(tab => tab.id === chraneneId);
    if (chraneny) orezane[orezane.length - 1] = chraneny;
  }

  return orezane;
};

/**
 * Která záložka má být aktivní po načtení stránky.
 *
 * Přednost má adresa v prohlížeči: po refreshi má člověk zůstat tam, kde byl.
 * Stav z databáze je totiž o debounce (2 s) pozadu a kdo si hned po otevření
 * sekce dal refresh, toho odnesl jinam. Teprve když adresa na žádnou záložku
 * nesedí, použije se poslední známá (localStorage) a nakonec ta z databáze.
 */
const aktivniPodleUrl = (tabs, cesta, zaloha) => {
  const podleUrl = cesta && cesta !== '/' ? tabs.find(tab => tab.path === cesta) : null;
  if (podleUrl) return podleUrl.id;
  if (zaloha && tabs.some(tab => tab.id === zaloha)) return zaloha;

  return tabs[0]?.id ?? 'dashboard';
};

// Helper function to check if user is authenticated
const isAuthenticated = () => {
  return !!api.getToken();
};

/**
 * Které oprávnění komponenta vyžaduje (viz plans/acl-role-plan.md).
 *
 * Stačí kterékoliv z uvedených. Komponenty, které tu nejsou (Dashboard,
 * UserProfile), vidí každý přihlášený.
 *
 * Kontroluje se i při obnově tabů — otevřené záložky se ukládají, takže
 * uživateli, kterému se právo odebere, by se jinak zakázaná sekce vrátila.
 * Skrytí v UI ale není ochrana, autoritativní kontrola je na backendu.
 */
const COMPONENT_PERMISSIONS = {
  OrdersList: ['order.view'],
  OrderDetail: ['order.view'],
  OrderForm: ['order.create', 'order.update'],
  CustomerManagement: ['crm.view'],
  CustomerDetail: ['crm.view'],
  CustomerForm: ['crm.create', 'crm.update'],
  MediaPricelist: ['media.view'],
  MediaPricelistDetail: ['media.view'],
  MediaForm: ['media.create', 'media.update'],
  ProjectForm: ['media.create', 'media.update'],
  RatecardForm: ['media.create', 'media.update'],
  PricelistItems: ['media.view'],
  InvoiceList: ['invoice.view'],
  InvoiceDetail: ['invoice.view'],
  PaymentMatching: ['invoice.payment_matching'],
  Spiglovani: ['spigl.view'],
  Statistics: ['statistics.view'],
  StatisticsSettings: ['statistics.view'],
  UserManagement: ['users.manage'],
  UserDetail: ['users.manage'],
  UserForm: ['users.manage'],
  Settings: ['settings.manage'],
};

// Save tabs to localStorage (instant, used as cache)
const saveTabsToLocalStorage = (tabs, activeTab) => {
  try {
    localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(tabs));
    localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, activeTab);
  } catch (e) {
    // Ignore storage errors
  }
};

// Load tabs from localStorage (fast fallback for page refresh)
const loadTabsFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(TABS_STORAGE_KEY);
    const storedActive = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY);
    if (stored) {
      const tabs = JSON.parse(stored);
      if (Array.isArray(tabs) && tabs.length > 0) {
        const orezane = orezNaLimit(tabs, storedActive);
        return { tabs: orezane, activeTab: storedActive || orezane[0].id };
      }
    }
  } catch (e) {
    // Ignore parse errors
  }
  return null;
};

// Clear tabs from localStorage only (DB state is preserved for next login)
const clearTabsLocalStorage = () => {
  localStorage.removeItem(TABS_STORAGE_KEY);
  localStorage.removeItem(ACTIVE_TAB_STORAGE_KEY);
};

// Load tab state from API (DB = source of truth for cross-session persistence)
const loadTabsFromApi = async () => {
  try {
    const result = await api.loadTabState();
    if (result?.data?.tabs && Array.isArray(result.data.tabs) && result.data.tabs.length > 0) {
      return { tabs: result.data.tabs, activeTab: result.data.activeTab || result.data.tabs[0].id };
    }
  } catch (e) {
    // Ignore API errors, fall back to defaults
  }
  return null;
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [currentUser, setCurrentUser] = useState(null);
  // Oprávnění přihlášeného (viz plans/acl-role-plan.md). Slouží jen ke skrývání
  // sekcí — autoritativní kontrola je vždycky na backendu.
  const [permissions, setPermissions] = useState([]);

  // Initialize tabs from localStorage cache (fast) — API will override on mount
  const initialState = isAuthenticated()
    ? (loadTabsFromLocalStorage() || { tabs: DEFAULT_TABS, activeTab: 'dashboard' })
    : { tabs: DEFAULT_TABS, activeTab: 'dashboard' };
  const [tabs, _setTabs] = useState(initialState.tabs);
  const [activeTab, _setActiveTab] = useState(initialState.activeTab);

  // Refs to track current state for synchronous reads in callbacks and beforeunload
  const tabsRef = useRef(initialState.tabs);
  const activeTabRef = useRef(initialState.activeTab);

  // Wrapped setters that keep refs in sync immediately (not deferred to useEffect)
  const setTabs = useCallback((value) => {
    const newVal = typeof value === 'function' ? value(tabsRef.current) : value;
    tabsRef.current = newVal;
    _setTabs(newVal);
  }, []);

  const setActiveTab = useCallback((value) => {
    const newVal = typeof value === 'function' ? value(activeTabRef.current) : value;
    activeTabRef.current = newVal;
    _setActiveTab(newVal);
  }, []);

  // Ref for debounced API save timer
  const saveTimerRef = useRef(null);
  // Counter to skip API saves when we just loaded from API (avoid echo-back).
  // Set to 2 because setTabs + setActiveTab trigger the persistence useEffect twice.
  const skipApiSaveCountRef = useRef(0);

  // Check auth on mount and load tab state from API (DB = source of truth)
  useEffect(() => {
    if (isAuthenticated()) {
      api.checkAuth()
        .then(async (result) => {
          setCurrentUser(result.data?.user);
          setPermissions(result.data?.permissions || []);
          setIsLoggedIn(true);

          // Load tab state from API (overrides localStorage cache)
          const apiState = await loadTabsFromApi();
          if (apiState) {
            // Záložka, kterou člověk otevřel těsně před refreshem, se do databáze
            // nemusela stihnout uložit — když na ni ukazuje adresa a v uloženém
            // stavu chybí, vrátí se zpátky z localStorage.
            let noveTaby = apiState.tabs;
            const cesta = window.location.pathname;
            if (cesta && cesta !== '/' && !noveTaby.some(tab => tab.path === cesta)) {
              const mistni = tabsRef.current.find(tab => tab.path === cesta);
              if (mistni) noveTaby = orezNaLimit(vlozZaDashboard(noveTaby, mistni), mistni.id);
            }

            const noveAktivni = aktivniPodleUrl(
              noveTaby,
              cesta,
              activeTabRef.current || apiState.activeTab,
            );

            skipApiSaveCountRef.current = 2; // Skip next 2 useEffect triggers (tabs + activeTab)
            setTabs(noveTaby);
            setActiveTab(noveAktivni);
            saveTabsToLocalStorage(noveTaby, noveAktivni);
          }
        })
        .catch(() => {
          api.setToken(null);
          setIsLoggedIn(false);
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for unauthorized events
  useEffect(() => {
    const handleUnauthorized = () => {
      setIsLoggedIn(false);
      setCurrentUser(null);
      setPermissions([]);
      navigate('/');
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate]);

  // Můj profil změnil jméno — hlavička ho zobrazuje vedle avataru, tak ho
  // přepíšeme bez reloadu (merge, ať se neztratí pole, která profil nevrací).
  useEffect(() => {
    const handleUserUpdated = (e) => {
      if (!e.detail) return;
      setCurrentUser((prev) => ({ ...prev, ...e.detail }));
    };
    window.addEventListener('user:updated', handleUserUpdated);
    return () => window.removeEventListener('user:updated', handleUserUpdated);
  }, []);

  const can = useCallback((...codes) => codes.some((c) => permissions.includes(c)), [permissions]);

  // Persist tabs: localStorage (instant) + API (debounced)
  useEffect(() => {
    if (isLoggedIn) {
      // Always save to localStorage immediately (cache for page refresh)
      saveTabsToLocalStorage(tabs, activeTab);

      // Skip API save if we just loaded from API (decrements counter for each trigger)
      if (skipApiSaveCountRef.current > 0) {
        skipApiSaveCountRef.current -= 1;
        return;
      }

      // Debounced save to API (DB persistence)
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = setTimeout(() => {
        api.saveTabState(tabs, activeTab).catch(() => {
          // Ignore API save errors silently
        });
      }, TAB_SAVE_DEBOUNCE_MS);
    }

    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, [tabs, activeTab, isLoggedIn]);

  // Flush tab state to API on browser close/refresh (before debounce expires)
  useEffect(() => {
    const handleBeforeUnload = () => {
      // localStorage is already up-to-date (written synchronously in the persistence effect).
      // Use sendBeacon to flush to API — include token in the body since
      // sendBeacon doesn't support custom headers.
      const token = api.getToken();
      if (token && tabsRef.current) {
        const url = `${api.getBaseUrl()}/tabs/save`;
        const payload = JSON.stringify({
          tabs: tabsRef.current,
          activeTab: activeTabRef.current,
          _token: token, // Auth token in body for beacon requests
        });
        navigator.sendBeacon(
          url,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Sync URL with active tab on mount / when active tab changes
  useEffect(() => {
    if (isLoggedIn) {
      const currentTab = tabs.find(tab => tab.id === activeTab);
      if (currentTab && location.pathname !== currentTab.path) {
        navigate(currentTab.path, { replace: true });
      }
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // On page load, try to match URL to an existing tab
  useEffect(() => {
    if (isLoggedIn && location.pathname !== '/') {
      const matchingTab = tabs.find(tab => tab.path === location.pathname);
      if (matchingTab && matchingTab.id !== activeTab) {
        setActiveTab(matchingTab.id);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = async (user, userPermissions = []) => {
    setIsLoggedIn(true);
    setCurrentUser(user);
    setPermissions(userPermissions);

    // Load saved tab state from API (restore where user left off)
    const apiState = await loadTabsFromApi();
    if (apiState) {
      const tabyPoLoginu = orezNaLimit(apiState.tabs, apiState.activeTab);
      skipApiSaveCountRef.current = 2; // Skip next 2 useEffect triggers (tabs + activeTab)
      setTabs(tabyPoLoginu);
      setActiveTab(apiState.activeTab);
      saveTabsToLocalStorage(tabyPoLoginu, apiState.activeTab);
      // Navigate to the active tab's path
      const activeTabObj = apiState.tabs.find(t => t.id === apiState.activeTab);
      navigate(activeTabObj ? activeTabObj.path : '/dashboard');
    } else {
      // No saved state — start fresh with Dashboard
      setTabs([...DEFAULT_TABS]);
      setActiveTab('dashboard');
      saveTabsToLocalStorage(DEFAULT_TABS, 'dashboard');
      navigate('/dashboard');
    }
  };

  const handleLogout = async () => {
    // Flush any pending debounced save immediately before logout
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    // Save current tab state to API before logging out
    try {
      await api.saveTabState(tabs, activeTab);
    } catch (e) {
      // Ignore errors
    }

    await api.logout();
    clearTabsLocalStorage();
    setIsLoggedIn(false);
    setCurrentUser(null);
    setTabs([...DEFAULT_TABS]);
    setActiveTab('dashboard');
    navigate('/');
  };

  // Redirect to dashboard if logged in and on root
  useEffect(() => {
    if (isLoggedIn && location.pathname === '/') {
      navigate('/dashboard');
    }
  }, [isLoggedIn, location.pathname, navigate]);

  // Update page title when active tab changes
  useEffect(() => {
    const currentTab = tabs.find(tab => tab.id === activeTab);
    if (currentTab) {
      document.title = `${currentTab.title} | RegVyd`;
    }
  }, [activeTab, tabs]);

  const openTab = useCallback((id, title, path, component, data = null) => {
    const prevTabs = tabsRef.current;
    const existingTab = prevTabs.find(tab => tab.id === id);

    if (existingTab) {
      // Update data if provided (e.g. to pass openProjectId to an already open tab)
      if (data) {
        setTabs(prevTabs.map(tab =>
          tab.id === id ? { ...tab, data: { ...tab.data, ...data } } : tab
        ));
      }
    } else {
      const newTab = { id, title, path, component, data };
      setTabs(orezNaLimit(vlozZaDashboard(prevTabs, newTab), id));
    }

    setActiveTab(id);
    navigate(path);
  }, [navigate]);

  const handleLogoClick = () => {
    openTab('dashboard', 'Dashboard', '/dashboard', 'Dashboard');
  };

  const closeTab = useCallback((tabId) => {
    // Compute new state from refs to avoid side-effects inside updaters
    const prevTabs = tabsRef.current;
    const prevActive = activeTabRef.current;

    let newTabs = prevTabs.filter(tab => tab.id !== tabId);
    if (newTabs.length === 0) {
      newTabs = [...DEFAULT_TABS];
    }

    let newActive = prevActive;
    if (prevActive === tabId) {
      // Zavřela se aktivní záložka — přejde se na tu, která je na jejím místě
      // (soused vpravo), jinak na předchozí. Nejnovější jsou vlevo, takže skok
      // na konec řady by člověka hodil k té nejstarší.
      const index = prevTabs.findIndex(tab => tab.id === tabId);
      const nextTab = newTabs[index] || newTabs[index - 1] || newTabs[0];
      newActive = nextTab ? nextTab.id : 'dashboard';
    }

    setTabs(newTabs);
    setActiveTab(newActive);

    // Navigate only if active tab changed
    if (newActive !== prevActive) {
      const nextTabObj = newTabs.find(t => t.id === newActive);
      navigate(nextTabObj ? nextTabObj.path : '/dashboard');
    }
  }, [navigate]);

  /**
   * Zavře všechny záložky napravo od dané. Nejnovější jsou vlevo, takže „napravo"
   * znamená „starší než tahle".
   */
  const closeTabsToRight = useCallback((tabId) => {
    const prevTabs = tabsRef.current;
    const index = prevTabs.findIndex(tab => tab.id === tabId);
    if (index < 0 || index === prevTabs.length - 1) return;

    const newTabs = prevTabs.slice(0, index + 1);
    const prevActive = activeTabRef.current;
    const newActive = newTabs.some(tab => tab.id === prevActive) ? prevActive : tabId;

    setTabs(newTabs);
    setActiveTab(newActive);

    if (newActive !== prevActive) {
      const nextTabObj = newTabs.find(tab => tab.id === newActive);
      navigate(nextTabObj ? nextTabObj.path : '/dashboard');
    }
  }, [navigate]);

  const closeOtherTabs = useCallback((keepTabId = null) => {
    // Close all tabs except dashboard and optionally the specified tab
    const prevTabs = tabsRef.current;
    let newTabs;

    if (keepTabId && keepTabId !== 'dashboard') {
      // Keep dashboard + the specified tab
      newTabs = prevTabs.filter(tab => tab.id === 'dashboard' || tab.id === keepTabId);
    } else {
      // Keep only dashboard
      newTabs = prevTabs.filter(tab => tab.id === 'dashboard');
    }

    if (newTabs.length === 0) {
      newTabs = [...DEFAULT_TABS];
    }

    // Determine new active tab
    const prevActive = activeTabRef.current;
    const activeStillExists = newTabs.some(t => t.id === prevActive);
    const newActive = activeStillExists ? prevActive : newTabs[newTabs.length - 1].id;

    setTabs(newTabs);
    setActiveTab(newActive);

    if (newActive !== prevActive) {
      const nextTabObj = newTabs.find(t => t.id === newActive);
      navigate(nextTabObj ? nextTabObj.path : '/dashboard');
    }
  }, [navigate]);

  const renderComponent = (componentName, tabData) => {
    const required = COMPONENT_PERMISSIONS[componentName];
    if (required && !required.some((code) => permissions.includes(code))) {
      return (
        <div className="container-fluid px-4 py-5 text-center text-muted">
          <i className="bi bi-lock display-5 d-block mb-3"></i>
          <h5 className="mb-1">Na tuhle sekci nemáš oprávnění</h5>
          <p className="mb-0">Pokud ji potřebuješ, řekni si správci o zařazení do příslušné skupiny.</p>
        </div>
      );
    }

    switch (componentName) {
      case 'Dashboard':
        return <Dashboard openTab={openTab} can={can} />;
      case 'OrdersList':
        return <OrdersList openTab={openTab} />;
      case 'OrderDetail':
        return <OrderDetail />;
      case 'UserManagement':
        return <UserManagement openTab={openTab} />;
      case 'UserDetail':
        return <UserDetail userId={tabData?.userId} openTab={openTab} closeTab={closeTab} activeTab={activeTab} />;
      case 'UserForm':
        return <UserForm openTab={openTab} closeTab={closeTab} activeTab={activeTab} />;
      case 'UserProfile':
        return <UserProfile />;
      case 'CustomerManagement':
        return <CustomerManagement openTab={openTab} />;
      case 'CustomerForm':
        return <CustomerForm openTab={openTab} closeTab={closeTab} activeTab={activeTab} />;
      case 'CustomerDetail':
        return <CustomerDetail customerId={tabData?.customerId} openTab={openTab} />;
      case 'MediaPricelist':
        return <MediaPricelist openTab={openTab} />;
      case 'MediaForm':
        return <MediaForm openTab={openTab} closeTab={closeTab} activeTab={activeTab} mediaId={tabData?.mediaId} />;
      case 'ProjectForm':
        return (
          <ProjectForm
            openTab={openTab}
            closeTab={closeTab}
            activeTab={activeTab}
            mediaId={tabData?.mediaId}
            mediaName={tabData?.mediaName}
            projectId={tabData?.projectId}
          />
        );
      case 'MediaPricelistDetail':
        return <MediaPricelistDetail mediaId={tabData?.mediaId} openTab={openTab} openProjectId={tabData?.openProjectId} />;
      case 'PricelistItems':
        return (
          <PricelistItems
            pricelistId={tabData?.pricelistId}
            pricelistName={tabData?.pricelistName}
            mediaName={tabData?.mediaName}
            mediaId={tabData?.mediaId}
            openTab={openTab}
          />
        );
      case 'RatecardForm':
        return (
          <RatecardForm
            openTab={openTab}
            closeTab={closeTab}
            activeTab={activeTab}
            mediaId={tabData?.mediaId}
            mediaName={tabData?.mediaName}
            ratecardId={tabData?.ratecardId}
          />
        );
      case 'OrderForm':
        return (
          <OrderForm
            openTab={openTab}
            closeTab={closeTab}
            activeTab={activeTab}
            orderId={tabData?.orderId}
            initialCustomerId={tabData?.customerId}
            initialCustomerName={tabData?.customerName}
          />
        );
      case 'InvoiceList':
        return <InvoiceList openTab={openTab} can={can} />;
      case 'InvoiceDetail':
        return <InvoiceDetail invoiceId={tabData?.invoiceId} openTab={openTab} can={can} />;
      case 'PaymentMatching':
        return <PaymentMatching openTab={openTab} />;
      case 'Settings':
        return <Settings openTab={openTab} />;
      case 'Spiglovani':
        return <Spiglovani />;
      case 'Statistics':
        return <Statistics openTab={openTab} />;
      case 'StatisticsSettings':
        return <StatisticsSettings />;
      default:
        return <Dashboard openTab={openTab} />;
    }
  };

  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="App">
      <ToastNotification />
      <ConfirmDialog />
      <Header onLogout={handleLogout} onLogoClick={handleLogoClick} user={currentUser} openTab={openTab} can={can} />
      <TabBar
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        closeTab={closeTab}
        closeOtherTabs={closeOtherTabs}
        closeTabsToRight={closeTabsToRight}
      />
      {/* Each tab gets its own scrollable content-area to preserve scroll position */}
      {tabs.map(tab => (
        <div
          key={tab.id}
          className="content-area"
          style={{ display: activeTab === tab.id ? 'block' : 'none' }}
        >
          {renderComponent(tab.component, tab.data)}
        </div>
      ))}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
